import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { storage, db } from "./firebase";
import { useAuth } from "./AuthContext";
import { useSubscription } from "./useSubscription";
import { logProjectAction, AUDIT_ACTIONS } from "./auditLogger";
import { calculateTotalPipelineValueUSD } from "./currencyUtils";
import "./styles.css"; 
import "./dashboard-improvements.css";

// Mock team members for the assignment dropdown
const TEAM_MEMBERS = [
    { name: "Unassigned", email: "" },
    { name: "Sarah Connor (Eng)", email: "sarah.c@example.com" },
    { name: "John Smith (Mgr)", email: "john.s@example.com" },
    { name: "Alex Jones (Compliance)", email: "alex.j@example.com" },
];

export default function Dashboard() { 
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();
  const { isPaidUser, isFreeUser, canEditFields } = useSubscription();
  const [tenders, setTenders] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archiveData, setArchiveData] = useState({ id: null, status: null, reason: "" });
  
  const [formData, setFormData] = useState({
    title: "",
    clientName: "",
    rfpNumber: "",
    value: "",
    currency: "USD",
    deadline: "",
    issuingOrg: "",
    contactPerson: "",
    description: "",
    assignedTo: "Unassigned", // NEW STATE
  });
  
  const [file, setFile] = useState(null); 
  const [loading, setLoading] = useState(false);

  // Handle logout
  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate("/login");
    }
  };

  // 1. SETUP REAL-TIME LISTENER
  useEffect(() => {
    // Only query if user profile is loaded with organizationId
    if (!userProfile?.organizationId) {
      setLoading(false);
      return;
    }

    // Query only tenders belonging to user's organization
    const q = query(
      collection(db, "tenders"),
      where("organizationId", "==", userProfile.organizationId)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const tendersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTenders(tendersData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching tenders:", error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [userProfile]);

  // 2. HANDLE INPUT CHANGES
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 3. ADD NEW TENDER
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that user profile and organizationId exist
      if (!userProfile?.organizationId) {
        alert("❌ Error: User profile not loaded or organization not found. Please refresh the page and try again.");
        setLoading(false);
        return;
      }

      let uploadedFiles = [];
      
      if (file && file.length > 0) {
        setStatusText(`Uploading ${file.length} file(s)...`);

        const uploadPromises = Array.from(file).map(async (singleFile) => {
          const fileRef = ref(storage, `tenders/${singleFile.name}`);
          await uploadBytes(fileRef, singleFile);
          const fileUrl = await getDownloadURL(fileRef);
          return {
            name: singleFile.name,
            url: fileUrl,
            size: singleFile.size,
          };
        });

        uploadedFiles = await Promise.all(uploadPromises);
      }

      setStatusText("Saving Tender...");
      
      const docRef = await addDoc(collection(db, "tenders"), {
        ...formData,
        organizationId: userProfile.organizationId, // Add organization ID for data isolation
        createdBy: currentUser.uid, // Track who created it
        files: uploadedFiles, 
        attachmentUrl: "", 
        status: "New Opportunities", 
        createdAt: new Date(),
        // Initial Revision Control
        currentRevision: 'Rev 2 (Open)',
        revisions: [{ name: 'Rev 0 (Initial)', timestamp: new Date().toISOString() }],
      });

      // Log project creation
      await logProjectAction(
        AUDIT_ACTIONS.PROJECT_CREATE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email,
          organizationId: userProfile.organizationId
        },
        docRef.id,
        formData.title,
        null,
        `Created project with client: ${formData.clientName}, value: ${formData.currency} ${formData.value}`
      );

      // Real-time listener will auto-update the tenders list
      setIsModalOpen(false);
      setFormData({
        title: "", clientName: "", rfpNumber: "", value: "", 
        currency: "USD", deadline: "", issuingOrg: "", contactPerson: "", description: "",
        assignedTo: "Unassigned" // Reset Assignment
      });
      setFile(null);
      setStatusText("");
      
    } catch (error) {
      console.error("Error:", error);
      alert("❌ Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 4. DELETE TENDER
  const handleDelete = async (e, id) => {
    e.stopPropagation(); 
    if (window.confirm("Delete this project?")) {
      try {
        const tender = tenders.find(t => t.id === id);
        await deleteDoc(doc(db, "tenders", id));
        
        // Log project deletion
        await logProjectAction(
          AUDIT_ACTIONS.PROJECT_DELETE,
          {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: userProfile?.displayName || currentUser.email
          },
          id,
          tender?.title || "Unknown Project",
          null,
          `Deleted project`
        );
        
        // Real-time listener will auto-update the tenders list
      } catch (error) {
        alert("Error deleting: " + error.message);
      }
    }
  };

  // 5. MOVE TENDER
  const handleMoveStatus = async (e, id, newStatus) => {
    e.stopPropagation(); // Stop click from opening details page
    try {
      const tender = tenders.find(t => t.id === id);
      const oldStatus = tender?.status;
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, { status: newStatus });
      
      // Log status change
      await logProjectAction(
        AUDIT_ACTIONS.PROJECT_STATUS_CHANGE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email
        },
        id,
        tender?.title || "Unknown Project",
        { from: oldStatus, to: newStatus },
        `Changed status from "${oldStatus}" to "${newStatus}"`
      );
      
      // Real-time listener will auto-update the tenders list
    } catch (error) {
      console.error("Error moving tender:", error);
      alert("Failed to move tender.");
    }
  };

  // 6. MARK AS WON/LOST AND ARCHIVE
  const handleMarkAndArchive = (e, id, newStatus) => {
    e.stopPropagation();
    setArchiveData({ id, status: newStatus, reason: "" });
    setIsArchiveModalOpen(true);
  };

  const confirmArchive = async () => {
    const { id, status, reason } = archiveData;
    const statusText = status === "Won" ? "Won" : "Lost";
    
    // Validate that reason is provided
    if (!reason || reason.trim() === "") {
      alert("Please provide a reason for archiving this tender.");
      return;
    }
    
    try {
      const tender = tenders.find(t => t.id === id);
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, { 
        status, 
        archived: true, 
        archivedAt: new Date(),
        archiveReason: reason.trim()
      });
      
      // Log archive action
      await logProjectAction(
        AUDIT_ACTIONS.PROJECT_ARCHIVE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email
        },
        id,
        tender?.title || "Unknown Project",
        { status, reason: reason.trim() },
        `Archived project as ${statusText}: ${reason.trim()}`
      );
      
      // Real-time listener will auto-update the tenders list
      setIsArchiveModalOpen(false);
      setArchiveData({ id: null, status: null, reason: "" });
    } catch (error) {
      console.error(`Error marking tender as ${statusText}:`, error);
      alert(`Failed to mark tender as ${statusText}.`);
    }
  };

  // 7. UNARCHIVE TENDER
  const handleUnarchiveTender = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Restore this tender to the dashboard?")) {
      try {
        const tender = tenders.find(t => t.id === id);
        const tenderRef = doc(db, "tenders", id);
        // Restore to Submitted status and unarchive
        await updateDoc(tenderRef, { 
          archived: false,
          status: "Submitted"
        });
        
        // Log unarchive action
        await logProjectAction(
          AUDIT_ACTIONS.PROJECT_UNARCHIVE,
          {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: userProfile?.displayName || currentUser.email
          },
          id,
          tender?.title || "Unknown Project",
          null,
          `Restored archived project to Submitted status`
        );
        
        // Real-time listener will auto-update the tenders list
      } catch (error) {
        console.error("Error unarchiving tender:", error);
        alert("Failed to restore tender.");
      }
    }
  };

  const getTendersByStatus = (status) => {
    let filtered = tenders.filter((t) => {
      // Filter by archived state - treat undefined as false (not archived)
      const isArchived = t.archived === true;
      if (showArchived) {
        return isArchived && t.status === status;
      } else {
        return !isArchived && t.status === status;
      }
    });
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((t) => 
        t.title?.toLowerCase().includes(query) ||
        t.clientName?.toLowerCase().includes(query) ||
        t.rfpNumber?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  // Helper function to get deadline status
  const getDeadlineStatus = (deadline) => {
    if (!deadline) return 'none';
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline < 0) return 'overdue';
    if (daysUntilDeadline <= 2) return 'urgent';
    if (daysUntilDeadline <= 7) return 'warning';
    return 'normal';
  };

  // Helper for submit button text (memoized for performance)
  const getSubmitButtonText = useCallback(() => {
    if (loading) return statusText;
    if (canEditFields) return "Create Tender";
    return "Upgrade to Create";
  }, [loading, statusText, canEditFields]);

  return (
    <div className="dashboard-container">
      {/* FREE USER NOTICE */}
      {isFreeUser && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>Free Plan</strong> - You can view the application, but all fields are disabled.
            <a 
              href="/subscription" 
              style={{ 
                marginLeft: '8px', 
                color: '#0066cc', 
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={(e) => { e.preventDefault(); navigate('/subscription'); }}
            >
              Upgrade to unlock all features
            </a>
          </div>
        </div>
      )}
      
      <div className="header">
        <div>
          <h1>📊 Dashboard</h1>
          <p style={{ color: "#666" }}>Manage your tender pipeline</p>
        </div>
        <div className="header-actions">
          <input 
            type="text" 
            className="search-input"
            placeholder="🔍 Search tenders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button 
            className="new-btn" 
            onClick={() => setIsModalOpen(true)}
            disabled={!canEditFields}
            style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
          >
            + New Tender
          </button>
          <button 
            className={showArchived ? "archive-toggle-btn active" : "archive-toggle-btn"}
            onClick={() => setShowArchived(!showArchived)}
            title={showArchived ? "Show Active Tenders" : "Show Archived Tenders"}
          >
            {showArchived ? "📂 Active View" : "📦 Archived View"}
          </button>
          
          {/* User Menu */}
          <div className="user-menu-container">
            <button 
              className="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
              aria-haspopup="true"
              aria-label="User menu"
            >
              <div className="user-avatar-small">
                {userProfile?.displayName?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || "?"}
              </div>
              <span className="user-name-display">
                {userProfile?.displayName || currentUser?.email?.split("@")[0]}
              </span>
              <span className="dropdown-arrow">▼</span>
            </button>
            
            {showUserMenu && (
              <div className="user-dropdown" role="menu">
                <div className="dropdown-header">
                  <strong>{userProfile?.displayName || "User"}</strong>
                  <span className="user-email">{currentUser?.email}</span>
                  <span className={`role-badge-small role-${userProfile?.role || "viewer"}`}>
                    {userProfile?.role || "viewer"}
                  </span>
                </div>
                <div className="dropdown-divider"></div>
                
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); navigate("/vendors"); }}>
                  🏭 Vendor Management
                </button>
                
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stats-row">
         <div className="stat-card stat-card-primary">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-label">Total Pipeline Value</div>
            <div className="stat-value">
              $ {calculateTotalPipelineValueUSD(tenders).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="stat-card stat-card-success">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-label">Active Tenders</div>
            <div className="stat-value">{tenders.filter(t => t && t.archived !== true).length}</div>
          </div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-label">Upcoming Deadlines</div>
            <div className="stat-value">
               {tenders.filter(t => t && t.archived !== true && t.deadline && new Date(t.deadline) > new Date()).length}
            </div>
          </div>
        </div>
      </div>

      <div className="pipeline-container">
        {(showArchived 
          ? ["Won", "Lost"] 
          : ["New Opportunities", "In Progress", "Submitted"]
        ).map((status, idx) => {
          // Map status to icon
          const statusIcons = {
            "Won": "🏆",
            "Lost": "❌",
            "New Opportunities": "🔵",
            "In Progress": "🟡",
            "Submitted": "🟢"
          };
          const statusIcon = statusIcons[status] || "📋";
          
          return (
          <div className="column" key={status}>
            <div className="column-title">
                {status} {statusIcon}
            </div>
            {getTendersByStatus(status).map((tender) => {
              const deadlineStatus = getDeadlineStatus(tender.deadline);
              return (
              <div 
                key={tender.id} 
                className={`tender-card ${deadlineStatus !== 'normal' && deadlineStatus !== 'none' ? 'deadline-' + deadlineStatus : ''}`}
                onClick={() => navigate(`/tender/${tender.id}`)}
                style={{cursor: 'pointer'}}
              >
                <button 
                  className="delete-btn" 
                  onClick={(e) => handleDelete(e, tender.id)}
                  disabled={!canEditFields}
                  style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                >
                  🗑️
                </button>
                
                {/* Deadline Warning Badge */}
                {deadlineStatus === 'urgent' && (
                  <div className="deadline-badge urgent">🔥 Due in {Math.ceil((new Date(tender.deadline) - new Date()) / (1000 * 60 * 60 * 24))} days</div>
                )}
                {deadlineStatus === 'warning' && (
                  <div className="deadline-badge warning">⚠️ Due in {Math.ceil((new Date(tender.deadline) - new Date()) / (1000 * 60 * 60 * 24))} days</div>
                )}
                {deadlineStatus === 'overdue' && (
                  <div className="deadline-badge overdue">❗ Overdue</div>
                )}
                
                <div className="card-title">{tender.title}</div>
                <span className="client-name">🏢 {tender.clientName}</span>
                <div className="card-desc">{tender.description?.substring(0, 60)}...</div>
                
                <div className="card-meta">
                   <span className="meta-item">💰 {tender.currency} {Number(tender.value || 0).toLocaleString()}</span>
                   <span className="meta-item">📅 {tender.deadline ? new Date(tender.deadline).toLocaleDateString() : "No Date"}</span>
                </div>
                
                {/* NEW: ASSIGNED TO BADGE */}
                {tender.assignedTo && tender.assignedTo !== "Unassigned" && (
                    <div className="assigned-badge">
                        👤 {tender.assignedTo}
                    </div>
                )}

                {/* --- MOVE BUTTONS --- */}
                <div className="move-btn-group">
                    {status === "New Opportunities" && (
                        <button 
                          className="move-btn move-btn-forward" 
                          onClick={(e) => handleMoveStatus(e, tender.id, "In Progress")}
                          disabled={!canEditFields}
                          style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                        >
                          Start →
                        </button>
                    )}
                    {status === "In Progress" && (
                        <>
                            <button 
                              className="move-btn move-btn-back" 
                              onClick={(e) => handleMoveStatus(e, tender.id, "New Opportunities")}
                              disabled={!canEditFields}
                              style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                            >
                              ← Back
                            </button>
                            <button 
                              className="move-btn move-btn-forward" 
                              onClick={(e) => handleMoveStatus(e, tender.id, "Submitted")}
                              disabled={!canEditFields}
                              style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                            >
                              Submit →
                            </button>
                        </>
                    )}
                    {status === "Submitted" && (
                        <>
                            <button 
                              className="move-btn move-btn-back" 
                              onClick={(e) => handleMoveStatus(e, tender.id, "In Progress")}
                              disabled={!canEditFields}
                              style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                            >
                              ← Reopen
                            </button>
                            <button 
                              className="move-btn move-btn-forward move-btn-won" 
                              onClick={(e) => handleMarkAndArchive(e, tender.id, "Won")}
                              disabled={!canEditFields}
                              style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                            >
                              🏆 Won
                            </button>
                            <button 
                              className="move-btn move-btn-forward move-btn-lost" 
                              onClick={(e) => handleMarkAndArchive(e, tender.id, "Lost")}
                              disabled={!canEditFields}
                              style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                            >
                              ❌ Lost
                            </button>
                        </>
                    )}
                    {showArchived && (
                        <button 
                          className="move-btn move-btn-forward" 
                          onClick={(e) => handleUnarchiveTender(e, tender.id)}
                          disabled={!canEditFields}
                          style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                        >
                          ↺ Restore
                        </button>
                    )}
                </div>
                {/* --- END MOVE BUTTONS --- */}

              </div>
            );
            })}
            {getTendersByStatus(status).length === 0 && (
                <div className="empty-state">
                    {searchQuery ? (
                      <>
                        <div className="empty-icon">🔍</div>
                        <div className="empty-text">No matching tenders</div>
                      </>
                    ) : (
                      <>
                        <div className="empty-icon">📭</div>
                        <div className="empty-text">Drop tenders here</div>
                      </>
                    )}
                </div>
            )}
          </div>
        );
        })}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create New Tender</h2>
            <form onSubmit={handleAddSubmit}>
              <div className="form-group">
                <label>Tender Title *</label>
                <input 
                  name="title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  required 
                  placeholder="e.g., City Infrastructure RFP"
                  disabled={!canEditFields}
                  style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input 
                    name="clientName" 
                    value={formData.clientName} 
                    onChange={handleChange} 
                    required 
                    placeholder="e.g., City of Springfield"
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label>RFP Number</label>
                  <input 
                    name="rfpNumber" 
                    value={formData.rfpNumber} 
                    onChange={handleChange} 
                    placeholder="e.g., RFP-2024-001"
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Estimated Value</label>
                  <input 
                    type="number" 
                    name="value" 
                    value={formData.value} 
                    onChange={handleChange} 
                    placeholder="500000"
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select 
                    name="currency" 
                    value={formData.currency} 
                    onChange={handleChange}
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="SAR">SAR - Saudi Riyal</option>
                    <option value="AED">AED - UAE Dirham</option>
                  </select>
                </div>
              </div>
              
              {/* NEW: ASSIGNED TO DROPDOWN */}
              <div className="form-grid">
                 <div className="form-group">
                    <label>Assigned To</label>
                    <select 
                      name="assignedTo" 
                      value={formData.assignedTo} 
                      onChange={handleChange}
                      disabled={!canEditFields}
                      style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                    >
                        {TEAM_MEMBERS.map(member => (
                            <option key={member.name} value={member.name}>
                                {member.name}
                            </option>
                        ))}
                    </select>
                 </div>
                 <div className="form-group">
                    <label>Submission Deadline *</label>
                    <input 
                      type="datetime-local" 
                      name="deadline" 
                      value={formData.deadline} 
                      onChange={handleChange} 
                      required
                      disabled={!canEditFields}
                      style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                    />
                 </div>
              </div>
              {/* END NEW ASSIGNED TO DROPDOWN */}
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Issuing Organization</label>
                  <input 
                    name="issuingOrg" 
                    value={formData.issuingOrg} 
                    onChange={handleChange} 
                    placeholder="e.g., Public Works Dept"
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input 
                    name="contactPerson" 
                    value={formData.contactPerson} 
                    onChange={handleChange} 
                    placeholder="e.g., John Smith"
                    disabled={!canEditFields}
                    style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  rows="3" 
                  placeholder="Brief description..."
                  disabled={!canEditFields}
                  style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'text' : 'not-allowed' }}
                />
              </div>
              
              <div className="form-group">
                <label>Attachment (Specs/BOQ)</label>
                <input 
                  type="file" 
                  multiple
                  onChange={(e) => setFile(e.target.files)}
                  disabled={!canEditFields}
                  style={{ opacity: canEditFields ? 1 : 0.5, cursor: canEditFields ? 'pointer' : 'not-allowed' }}
                />
              </div>

              <div style={{display:'flex', gap:'10px', marginTop: '20px'}}>
                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)} style={{flex:1}}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="new-btn" 
                  style={{
                    flex:1, 
                    opacity: (canEditFields && !loading) ? 1 : 0.5, 
                    cursor: (canEditFields && !loading) ? 'pointer' : 'not-allowed'
                  }} 
                  disabled={loading || !canEditFields}
                >
                  {getSubmitButtonText()}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ARCHIVE MODAL */}
      {isArchiveModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Archive Tender - {archiveData.status === "Won" ? "🏆 Won" : "❌ Lost"}</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              Please provide a reason for marking this tender as {archiveData.status === "Won" ? "Won" : "Lost"}. 
              This will help with future learning and analysis.
            </p>
            
            <div className="form-group">
              <label>Reason / Comments *</label>
              <textarea 
                value={archiveData.reason}
                onChange={(e) => setArchiveData({ ...archiveData, reason: e.target.value })}
                rows="4"
                placeholder={archiveData.status === "Won" 
                  ? "e.g., Best pricing, strong technical solution, good client relationship..." 
                  : "e.g., Price too high, missing key requirements, competitor advantage..."}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>

            <div style={{display:'flex', gap:'10px', marginTop: '20px'}}>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={() => {
                  setIsArchiveModalOpen(false);
                  setArchiveData({ id: null, status: null, reason: "" });
                }} 
                style={{flex:1}}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="new-btn" 
                onClick={confirmArchive}
                style={{flex:1}}
              >
                Confirm {archiveData.status === "Won" ? "Won" : "Lost"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
